import {NotificationEventType} from '@/types/notification'

 

type I18nNotifications = {
    eventAccessRequestCreated: string
    eventAccessRequestApproved: string
    eventAccessRequestRejected: string
    eventAccessRequestCancelled: string
    eventAccessProfileAssignmentGranted: string
    eventAccessProfileAssignmentGrantedByRule: string
    eventAccessProfileAssignmentRevoked: string
    eventAccessProfileAssignmentRestored: string
    eventFunctionGroupRequestCreated: string
    eventFunctionGroupRequestApproved: string
    eventFunctionGroupRequestRejected: string
    eventFunctionGroupRequestCancelled: string
    eventFunctionGroupAssignmentGranted: string
    eventFunctionGroupAssignmentGrantedByRule: string
    eventFunctionGroupAssignmentRevoked: string
    eventFunctionGroupAssignmentRestored: string
    eventAccessProfileAssignmentExpiring: string
    eventFunctionGroupAssignmentExpiring: string
    eventIdentitySuspended: string
    eventIdentityTerminated: string
    eventProvisioningFailed: string
    eventCertificationCampaignStarted: string
    eventCertificationItemPending: string
    eventCertificationItemOverdue: string
    eventCertificationCampaignCompleted: string
    eventReportDelivered: string
}

const eventTypeKeyMap: Record<string, keyof I18nNotifications> = {
    [NotificationEventType.ACCESS_REQUEST_CREATED]: 'eventAccessRequestCreated',
    [NotificationEventType.ACCESS_REQUEST_APPROVED]: 'eventAccessRequestApproved',
    [NotificationEventType.ACCESS_REQUEST_REJECTED]: 'eventAccessRequestRejected',
    [NotificationEventType.ACCESS_REQUEST_CANCELLED]: 'eventAccessRequestCancelled',
    [NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_GRANTED]: 'eventAccessProfileAssignmentGranted',
    [NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_GRANTED_BY_RULE]: 'eventAccessProfileAssignmentGrantedByRule',
    [NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_REVOKED]: 'eventAccessProfileAssignmentRevoked',
    [NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_RESTORED]: 'eventAccessProfileAssignmentRestored',
    [NotificationEventType.FUNCTION_GROUP_REQUEST_CREATED]: 'eventFunctionGroupRequestCreated',
    [NotificationEventType.FUNCTION_GROUP_REQUEST_APPROVED]: 'eventFunctionGroupRequestApproved',
    [NotificationEventType.FUNCTION_GROUP_REQUEST_REJECTED]: 'eventFunctionGroupRequestRejected',
    [NotificationEventType.FUNCTION_GROUP_REQUEST_CANCELLED]: 'eventFunctionGroupRequestCancelled',
    [NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_GRANTED]: 'eventFunctionGroupAssignmentGranted',
    [NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_GRANTED_BY_RULE]: 'eventFunctionGroupAssignmentGrantedByRule',
    [NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_REVOKED]: 'eventFunctionGroupAssignmentRevoked',
    [NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_RESTORED]: 'eventFunctionGroupAssignmentRestored',
    [NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_EXPIRING]: 'eventAccessProfileAssignmentExpiring',
    [NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_EXPIRING]: 'eventFunctionGroupAssignmentExpiring',
    [NotificationEventType.IDENTITY_SUSPENDED]: 'eventIdentitySuspended',
    [NotificationEventType.IDENTITY_TERMINATED]: 'eventIdentityTerminated',
    [NotificationEventType.PROVISIONING_FAILED]: 'eventProvisioningFailed',
    [NotificationEventType.CERTIFICATION_CAMPAIGN_STARTED]: 'eventCertificationCampaignStarted',
    [NotificationEventType.CERTIFICATION_ITEM_PENDING]: 'eventCertificationItemPending',
    [NotificationEventType.CERTIFICATION_ITEM_OVERDUE]: 'eventCertificationItemOverdue',
    [NotificationEventType.CERTIFICATION_CAMPAIGN_COMPLETED]: 'eventCertificationCampaignCompleted',
    [NotificationEventType.REPORT_DELIVERED]: 'eventReportDelivered',
}

export function getEventTypeLabel(eventType: string, i18nNotifications: I18nNotifications): string {
    const key = eventTypeKeyMap[eventType]
    return key ? (i18nNotifications[key] as string) : eventType
}

/* eslint-disable i18next/no-literal-string -- technical category constants, not user-visible text */
export const EventCategoryValues = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
} as const
/* eslint-enable i18next/no-literal-string */

export type EventCategory = typeof EventCategoryValues[keyof typeof EventCategoryValues]

export function getEventCategory(eventType: string): EventCategory {
    switch (eventType) {
        case NotificationEventType.ACCESS_REQUEST_APPROVED:
        case NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_GRANTED:
        case NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_GRANTED_BY_RULE:
        case NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_RESTORED:
        case NotificationEventType.FUNCTION_GROUP_REQUEST_APPROVED:
        case NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_GRANTED:
        case NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_GRANTED_BY_RULE:
        case NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_RESTORED:
            return 'success'
        case NotificationEventType.ACCESS_REQUEST_REJECTED:
        case NotificationEventType.ACCESS_REQUEST_CANCELLED:
        case NotificationEventType.FUNCTION_GROUP_REQUEST_REJECTED:
        case NotificationEventType.FUNCTION_GROUP_REQUEST_CANCELLED:
        case NotificationEventType.PROVISIONING_FAILED:
        case NotificationEventType.IDENTITY_TERMINATED:
            return 'error'
        case NotificationEventType.IDENTITY_SUSPENDED:
        case NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_REVOKED:
        case NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_REVOKED:
        case NotificationEventType.ACCESS_PROFILE_ASSIGNMENT_EXPIRING:
        case NotificationEventType.FUNCTION_GROUP_ASSIGNMENT_EXPIRING:
            return 'warning'
        default:
            return 'info'
    }
}

const categoryToBadgeVariant: Record<EventCategory, 'success' | 'destructive' | 'warning' | 'outline'> = {
    success: 'success',
    error: 'destructive',
    warning: 'warning',
    info: 'outline',
}

export function getEventTypeBadgeVariant(eventType: string): 'success' | 'destructive' | 'warning' | 'outline' {
    return categoryToBadgeVariant[getEventCategory(eventType)]
}
